import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import type { SaveTimetableSlotInput, StaffRole, StaffRow, SubjectRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Timetable builder.
 * Pick a section (derived from real students) → 6-day × 8-period grid.
 * Each cell picks a subject and an active teacher. Save replaces the section's
 * slots atomically. Clash-detection ships next iteration.
 */
const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
const ROLE_LABELS: Record<StaffRole, string> = {
  principal: 'Principal', vice_principal: 'Vice principal',
  coordinator: 'Coordinator', teacher: 'Teacher', clerk: 'Clerk', support: 'Support',
};

interface CellPick { subject_id: string; teacher_id: string | null }

export function TimetableBuilderPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [subjectOpen, setSubjectOpen] = useState(false);

  const students = useQuery({
    queryKey: ['students', institution, {}],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution,
  });
  const sections = useMemo(() => {
    const set = new Map<string, { grade: string; section: string; count: number }>();
    for (const s of students.data?.rows ?? []) {
      const key = `${s.grade}::${s.section}`;
      const cur = set.get(key) ?? { grade: s.grade, section: s.section, count: 0 };
      cur.count++;
      set.set(key, cur);
    }
    return Array.from(set.values()).sort((a, b) =>
      a.grade === b.grade ? a.section.localeCompare(b.section) : a.grade.localeCompare(b.grade),
    );
  }, [students.data]);

  const [selected, setSelected] = useState<{ grade: string; section: string } | null>(null);
  useEffect(() => {
    if (!selected && sections.length > 0) setSelected(sections[0]!);
  }, [sections, selected]);

  const subjects = useQuery({
    queryKey: ['subjects', institution],
    queryFn: () => productApi.listSubjects(institution),
    enabled: !!institution,
  });
  const staff = useQuery({
    queryKey: ['staff', institution],
    queryFn: () => productApi.listStaff(institution),
    enabled: !!institution,
  });
  const timetable = useQuery({
    queryKey: ['timetable', institution, selected?.grade, selected?.section],
    queryFn: () => productApi.getTimetable(institution, selected!.grade, selected!.section),
    enabled: !!selected,
  });

  const [grid, setGrid] = useState<Record<string, CellPick | null>>({});
  useEffect(() => {
    if (!timetable.data) return;
    const g: Record<string, CellPick | null> = {};
    for (const s of timetable.data.slots) {
      g[`${s.day_of_week}-${s.period}`] = { subject_id: s.subject_id, teacher_id: s.teacher_id };
    }
    setGrid(g);
  }, [timetable.data]);

  const save = useMutation({
    mutationFn: () => {
      const slots: SaveTimetableSlotInput[] = [];
      for (const [key, cell] of Object.entries(grid)) {
        if (!cell) continue;
        const [d, p] = key.split('-').map(Number);
        slots.push({ day_of_week: d!, period: p!, subject_id: cell.subject_id, teacher_id: cell.teacher_id });
      }
      return productApi.saveTimetable(institution, { grade: selected!.grade, section: selected!.section, slots });
    },
    onSuccess: (r) => {
      void message.success(`Saved ${r.saved} slot${r.saved === 1 ? '' : 's'}`);
      void qc.invalidateQueries({ queryKey: ['timetable', institution, selected?.grade, selected?.section] });
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Save failed'),
  });

  const teachers = (staff.data?.rows ?? []).filter((s) => s.status === 'active');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Timetable</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            One section at a time — 6 days × 8 periods. Save replaces the whole grid atomically.
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setSubjectOpen(true)}>Add subject</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={!selected || save.isPending}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save timetable
          </Button>
        </Space>
      </div>

      {sections.length === 0 && (
        <Alert type="warning" showIcon message="No students / sections on file yet. Add students in the directory first." />
      )}

      {sections.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>Section:</Typography.Text>
          {sections.map((s) => {
            const active = selected?.grade === s.grade && selected?.section === s.section;
            return (
              <Tag.CheckableTag
                key={`${s.grade}-${s.section}`}
                checked={active}
                onChange={() => setSelected(s)}
              >
                {s.grade}{s.section ? `-${s.section}` : ''} · {s.count}
              </Tag.CheckableTag>
            );
          })}
        </div>
      )}

      {subjects.data && subjects.data.rows.length === 0 && (
        <Alert
          type="info"
          showIcon
          message={<span>No subjects yet. <a onClick={() => setSubjectOpen(true)}>Add the first subject</a> before building the grid.</span>}
        />
      )}

      {selected && (
        <Card size="small" title={`${selected.grade}${selected.section ? `-${selected.section}` : ''}`}>
          {timetable.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
          {(subjects.data?.rows.length ?? 0) === 0 ? (
            <Empty description="Add subjects first" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={cellHead()}>Day / Period</th>
                    {PERIODS.map((p) => (
                      <th key={p} style={cellHead()}>P{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((d) => (
                    <tr key={d.value}>
                      <td style={cellHead()}>{d.label}</td>
                      {PERIODS.map((p) => {
                        const key = `${d.value}-${p}`;
                        const cell = grid[key];
                        return (
                          <td key={p} style={cellBody()}>
                            <SlotCell
                              value={cell ?? null}
                              subjects={subjects.data?.rows ?? []}
                              teachers={teachers}
                              onChange={(v) => setGrid((g) => ({ ...g, [key]: v }))}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <AddSubjectModal
        open={subjectOpen}
        onClose={() => setSubjectOpen(false)}
        institution={institution}
        onCreated={() => {
          setSubjectOpen(false);
          void qc.invalidateQueries({ queryKey: ['subjects', institution] });
          void message.success('Subject added');
        }}
      />
    </Space>
  );
}

function SlotCell({
  value, subjects, teachers, onChange,
}: {
  value: CellPick | null;
  subjects: SubjectRow[];
  teachers: StaffRow[];
  onChange: (v: CellPick | null) => void;
}) {
  return (
    <div style={{ minWidth: 130 }}>
      <Select
        size="small"
        value={value?.subject_id}
        placeholder="—"
        style={{ width: '100%' }}
        options={subjects.map((s) => ({ value: s.id, label: s.code }))}
        onChange={(v) => onChange({ subject_id: v, teacher_id: value?.teacher_id ?? null })}
        allowClear
        onClear={() => onChange(null)}
      />
      <Select
        size="small"
        value={value?.teacher_id ?? undefined}
        placeholder="teacher"
        style={{ width: '100%', marginTop: 4 }}
        options={teachers.map((t) => ({ value: t.id, label: `${t.name} · ${ROLE_LABELS[t.role]}` }))}
        onChange={(v) => value && onChange({ ...value, teacher_id: v ?? null })}
        allowClear
        disabled={!value?.subject_id}
      />
      {value && (
        <div style={{ marginTop: 2, textAlign: 'right' }}>
          <Popconfirm title="Clear this slot?" onConfirm={() => onChange(null)}>
            <Button size="small" type="text" icon={<DeleteOutlined />} danger style={{ padding: 0 }} />
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

function AddSubjectModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ name: string; code: string }>();
  const m = useMutation({
    mutationFn: (v: { name: string; code: string }) => productApi.createSubject(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add subject" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate({ name: v.name.trim(), code: v.code.trim().toUpperCase() })}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Mathematics" autoFocus />
        </Form.Item>
        <Form.Item name="code" label="Short code" rules={[{ required: true }]}>
          <Input placeholder="MATH" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function cellHead(): React.CSSProperties {
  return { border: '1px solid #eeeef1', padding: 6, background: '#f7f7f5', textAlign: 'left', fontWeight: 600, color: '#26304a' };
}
function cellBody(): React.CSSProperties {
  return { border: '1px solid #eeeef1', padding: 4, verticalAlign: 'top' };
}
function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
